/**
 * 홈페이지 첫 화면 (로그인 전) — 소개 · 업무 흐름(사무실→공장→창고→물류) · 무료 체험 · 1:1 문의 · 운영사 정보
 */
import React, { useEffect, useState } from 'react';
import { sendInquiry, fetchFormToken, errMsg } from '../utils/api';
import { TermsDialog } from './Terms';

const BG = '#EDF1F6';      // 일러스트 배경색과 같음
const BLUE = '#3182F6';
const INK = '#191F28';
const SUB = '#4E5968', MUTE = '#8B95A1';

const STEPS = [
  { img: '/img/office.webp', title: '사무실', sub: '도면 · 견적', text: '가로·세로만 넣으면 벽·천장 패널이 음양수까지 자동으로. 문·하리·칸막이도 끌어서 배치하고 생산지시서·재단표를 바로 인쇄합니다.' },
  { img: '/img/factory.webp', title: '공장', sub: '재단 · 생산', text: '재단표(표준/재단·철판 사이즈)와 패널 번호 라벨(QR)로 작업. 생산이 끝난 패널은 폰으로 QR을 찍어 완료 처리합니다.' },
  { img: '/img/warehouse.webp', title: '창고', sub: 'QR 입고 · 보관', text: '보관 위치와 함께 입고. 어느 현장 패널이 어디에 몇 장 있는지 작업 현황에서 한눈에 봅니다.' },
  { img: '/img/logistics.webp', title: '물류', sub: '배차 · 출고 · 설치', text: '차량·기사·운임을 정해 배차하고 적재표를 인쇄. 현장 도착·설치까지 견적 상태가 자동으로 따라갑니다.' },
];

const btn = (primary: boolean): React.CSSProperties => ({
  height: 54, padding: '0 24px', fontSize: 17, fontWeight: 700, borderRadius: 14, cursor: 'pointer', border: 'none',
  background: primary ? BLUE : '#E8F3FF', color: primary ? '#fff' : BLUE,
});

const Inquiry: React.FC = () => {
  const [f, setF] = useState({ name: '', company: '', tel: '', email: '', content: '', agree: false });
  const [hp, setHp] = useState('');
  const [formToken, setFormToken] = useState('');
  const newToken = () => fetchFormToken().then(r => setFormToken(r.data.token)).catch(() => {});
  useEffect(() => { newToken(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [terms, setTerms] = useState(false);
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setMsg(null);
    try {
      await sendInquiry({ ...f, website: hp, formToken });
      newToken();
      setMsg({ ok: true, text: '문의를 보냈습니다. 확인 후 이메일이나 전화로 답변드리겠습니다.' });
      setF({ name: '', company: '', tel: '', email: '', content: '', agree: false });
    } catch (ex) { setMsg({ ok: false, text: errMsg(ex) }); } finally { setBusy(false); }
  };
  const input: React.CSSProperties = { width: '100%', padding: '13px 14px', fontSize: 15, border: '1.5px solid transparent', borderRadius: 12, boxSizing: 'border-box', background: '#F2F4F6', color: INK };
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setF({ ...f, [k]: e.target.value });
  return (
    <form onSubmit={submit} style={{ background: '#fff', borderRadius: 24, padding: 28 }}>
      <input value={hp} onChange={e => setHp(e.target.value)} name="website" tabIndex={-1} autoComplete="off" aria-hidden="true"
        style={{ position: 'absolute', left: -9999, width: 1, height: 1, opacity: 0 }} />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
        <input placeholder="이름 *" value={f.name} onChange={set('name')} style={input} autoComplete="name" />
        <input placeholder="회사명" value={f.company} onChange={set('company')} style={input} autoComplete="organization" />
        <input placeholder="연락처" value={f.tel} onChange={set('tel')} style={input} autoComplete="tel" inputMode="tel" />
        <input placeholder="이메일 (답변 받을 곳)" type="email" value={f.email} onChange={set('email')} style={input} autoComplete="email" />
      </div>
      <textarea placeholder="문의 내용 * — 사용 문의, 기능 제안, 불편한 점 무엇이든" value={f.content} onChange={set('content')} rows={5}
        style={{ ...input, marginTop: 10, resize: 'vertical', fontFamily: 'inherit' }} />
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: SUB, margin: '14px 0' }}>
        <input type="checkbox" checked={f.agree} onChange={e => setF({ ...f, agree: e.target.checked })} />
        답변을 위한 개인정보(이름·연락처·이메일) 수집·이용에 동의합니다
        <button type="button" onClick={() => setTerms(true)} style={{ background: 'none', border: 'none', color: BLUE, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>보기</button>
      </label>
      {msg && <div style={{ fontSize: 14, marginBottom: 10, color: msg.ok ? '#03B26C' : '#F04452' }}>{msg.ok ? '✓ ' : '⚠ '}{msg.text}</div>}
      <button type="submit" disabled={busy} style={{ ...btn(true), width: '100%' }}>{busy ? '보내는 중…' : '문의 보내기'}</button>
      {terms && <TermsDialog onClose={() => setTerms(false)} />}
    </form>
  );
};

const Landing: React.FC<{ onDemo: () => void; onLogin: () => void; onSignup: () => void }> = ({ onDemo, onLogin, onSignup }) => {
  const [terms, setTerms] = useState(false);
  const wrap: React.CSSProperties = { maxWidth: 1180, margin: '0 auto', padding: '0 20px' };
  return (
    <div style={{ height: '100vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', background: '#fff', color: INK }}>
      {/* 위 막대 */}
      <div style={{ position: 'sticky', top: 0, zIndex: 10, background: 'rgba(255,255,255,0.92)', backdropFilter: 'blur(6px)', borderBottom: '1px solid #F2F4F6' }}>
        <div style={{ ...wrap, display: 'flex', alignItems: 'center', gap: 10, height: 60 }}>
          <img src="/icon.svg" alt="" width={28} height={28} /><b style={{ fontSize: 20, color: INK, letterSpacing: -0.4, whiteSpace: 'nowrap' }}>Panel Draw</b>
          {window.innerWidth >= 560 && <span style={{ fontSize: 13, color: MUTE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>냉동창고 패널 도면·생산·출고</span>}
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
            <a href="/guide/" target="_blank" rel="noopener noreferrer" style={{ height: 40, padding: '0 12px', fontSize: 14, color: SUB, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>사용 안내</a>
            <button onClick={onLogin} style={{ ...btn(false), height: 40, padding: '0 16px', fontSize: 14, borderRadius: 10, background: '#F2F4F6', color: '#333D4B', whiteSpace: 'nowrap' }}>로그인</button>
            <button onClick={onSignup} style={{ ...btn(true), height: 40, padding: '0 16px', fontSize: 14, borderRadius: 10, whiteSpace: 'nowrap' }}>가입 신청</button>
          </div>
        </div>
      </div>

      {/* 첫 화면 */}
      <section style={{ background: BG }}>
        <div style={{ ...wrap, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 24, padding: '40px 20px 24px' }}>
          <div style={{ flex: '1 1 360px', minWidth: 0 }}>
            <h1 style={{ fontSize: 'clamp(30px, 4.4vw, 48px)', lineHeight: 1.25, margin: '0 0 18px', letterSpacing: -1.2, fontWeight: 800 }}>
              냉동창고 패널,<br />도면부터 <span style={{ whiteSpace: 'nowrap' }}><span style={{ color: BLUE }}>출고까지</span> 한 번에</span>
            </h1>
            <p style={{ fontSize: 18, lineHeight: 1.7, color: SUB, margin: '0 0 28px' }}>
              치수만 넣으면 벽·천장 패널이 자동으로 나뉘고, 재단표·라벨·작업지시·배차까지 이어집니다.
              사무실·공장·창고·현장이 같은 패널 번호로 일합니다.
            </p>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <button onClick={onDemo} style={btn(true)}>무료로 도면 그려보기 →</button>
              <button onClick={onSignup} style={btn(false)}>회사 가입 신청</button>
            </div>
            <div style={{ fontSize: 14, color: MUTE, marginTop: 12 }}>가입 없이 바로 그려볼 수 있습니다 · 저장·작업 관리는 가입 후</div>
          </div>
          <div style={{ flex: '1 1 520px', minWidth: 0 }}>
            <picture>
              <source type="image/webp" srcSet="/img/hero-sm.webp 800w, /img/hero.webp 1600w, /img/hero@2x.webp 3200w" sizes="(max-width: 900px) 100vw, 620px" />
              <img src="/img/hero.jpg" alt="사무실·공장·창고·물류가 있는 냉동창고 패널 회사 단지 그림" width={1600} height={960}
                style={{ width: '100%', height: 'auto', display: 'block' }} />
            </picture>
          </div>
        </div>
      </section>

      {/* 업무 흐름 */}
      <section style={{ ...wrap, padding: '48px 20px 24px' }}>
        <h2 style={{ fontSize: 30, margin: '0 0 10px', textAlign: 'center', fontWeight: 800, letterSpacing: -0.8 }}>사무실에서 현장까지, 같은 번호로</h2>
        <p style={{ textAlign: 'center', color: MUTE, margin: '0 0 32px', fontSize: 16 }}>도면에서 붙은 패널 번호(W01·C01…)가 재단표·라벨·창고·적재표까지 그대로 이어집니다</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 }}>
          {STEPS.map((s, i) => (
            <div key={s.title} style={{ borderRadius: 24, overflow: 'hidden', background: '#F9FAFB' }}>
              <div style={{ background: BG, aspectRatio: '4 / 3' }}>
                <img src={s.img} alt={`${s.title} — ${s.sub}`} loading="lazy" width={640} height={480} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              <div style={{ padding: '18px 20px 22px' }}>
                <div style={{ fontSize: 13, color: BLUE, fontWeight: 700 }}>STEP {i + 1}</div>
                <div style={{ fontSize: 21, fontWeight: 800, margin: '4px 0 6px', letterSpacing: -0.4 }}>{s.title} <span style={{ fontSize: 14, color: MUTE, fontWeight: 600 }}>{s.sub}</span></div>
                <div style={{ fontSize: 15, lineHeight: 1.65, color: SUB }}>{s.text}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 체험 띠 */}
      <section style={{ ...wrap, padding: '24px 20px' }}>
        <div style={{ background: BLUE, color: '#fff', borderRadius: 24, padding: '34px 32px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 18 }}>
          <div style={{ flex: '1 1 300px' }}>
            <div style={{ fontSize: 24, fontWeight: 800, letterSpacing: -0.5 }}>지금 바로 도면을 그려보세요</div>
            <div style={{ opacity: 0.9, marginTop: 6 }}>가로·세로를 넣고 [도면 생성] — 문·하리·칸막이 배치, 패널 수정, 인쇄까지 가입 없이 써볼 수 있습니다.</div>
          </div>
          <button onClick={onDemo} style={{ ...btn(false), background: '#fff' }}>무료로 그려보기 →</button>
        </div>
      </section>

      {/* 1:1 문의 */}
      <section style={{ background: BG, marginTop: 24 }}>
        <div style={{ ...wrap, padding: '44px 20px', display: 'flex', flexWrap: 'wrap', gap: 28 }}>
          <div style={{ flex: '1 1 260px' }}>
            <h2 style={{ fontSize: 30, margin: '0 0 12px', fontWeight: 800, letterSpacing: -0.8 }}>1:1 문의 · 의견 보내기</h2>
            <p style={{ color: SUB, lineHeight: 1.75, margin: 0, fontSize: 16 }}>
              써보시고 불편한 점, 필요한 기능, 도입 문의 무엇이든 보내 주세요.<br />
              확인 후 이메일이나 전화로 답변드립니다.
            </p>
            <p style={{ color: MUTE, fontSize: 14, marginTop: 16 }}>메일: info@hjbrain.com</p>
          </div>
          <div style={{ flex: '2 1 420px', minWidth: 0 }}><Inquiry /></div>
        </div>
      </section>

      {/* 운영사 정보 */}
      <footer style={{ borderTop: '1px solid #F2F4F6', padding: '22px 0 30px', fontSize: 13, color: MUTE, lineHeight: 1.8 }}>
        <div style={wrap}>
          <a href="https://www.hjbrain.com" target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', marginBottom: 6 }}>
            <img src="/img/hjbrain-logo.svg" alt="hjbrain" height={20} style={{ height: 20, width: 'auto', display: 'block', opacity: 0.85 }} />
          </a><br />
          <b style={{ color: INK }}>주식회사 에이치제이브레인</b> · 대표 이선희 · 사업자등록번호 821-87-01635<br />
          경기도 용인시 기흥구 구갈로60번길 9-1, 6층 602-A100호(구갈동, 라파빌딩) · 문의 info@hjbrain.com<br />
          <button onClick={() => setTerms(true)} style={{ background: 'none', border: 'none', padding: 0, color: SUB, cursor: 'pointer', textDecoration: 'underline', fontSize: 13 }}>
            이용약관 · 개인정보 처리방침</button> · © Panel Draw
        </div>
      </footer>
      {terms && <TermsDialog onClose={() => setTerms(false)} />}
    </div>
  );
};

export default Landing;
